import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GuidLink } from './GuidLink';
import { getOptionSetLabel } from '../../lib/option-sets';
import { displayValue } from '../../lib/format-value';
import type { ColumnConfig } from '../../lib/entity-config';

interface CuroAttribute {
  Key: string;
  Value: any;
}

interface CuroRecord {
  Id: string;
  LogicalName: string;
  Attributes: CuroAttribute[];
}

interface EntityTableProps {
  records: CuroRecord[];
  columns: ColumnConfig[];
  entityName: string;
  primaryKeyField: string;
  isLoading?: boolean;
}

function getAttrValue(record: CuroRecord, key: string): any {
  const attr = record.Attributes.find(a => a.Key === key);
  return attr?.Value;
}

function formatCellValue(value: any, column: ColumnConfig): React.ReactNode {
  if (value === null || value === undefined) return '-';

  switch (column.type) {
    case 'date':
      try {
        return new Date(value).toLocaleDateString('en-GB');
      } catch {
        return String(value);
      }
    case 'number':
      if (typeof value === 'number') {
        return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return displayValue(value);
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'optionSet':
      if (typeof value === 'object' && value !== null && 'Value' in value) {
        return getOptionSetLabel(column.optionSetKey ?? column.key, value.Value);
      }
      if (typeof value === 'number') {
        return getOptionSetLabel(column.optionSetKey ?? column.key, value);
      }
      return displayValue(value);
    case 'fk':
      if (typeof value === 'object' && value !== null && 'Id' in value) {
        return <GuidLink id={value.Id} name={value.Name} entityName={column.targetEntity ?? ''} />;
      }
      return displayValue(value);
    default:
      return displayValue(value);
  }
}

export function EntityTable({ records, columns, entityName, primaryKeyField, isLoading }: EntityTableProps) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = useMemo(() => {
    if (!search) return records;
    const q = search.toLowerCase();
    return records.filter(record =>
      record.Attributes.some(a =>
        displayValue(a.Value).toLowerCase().includes(q)
      )
    );
  }, [records, search]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const valA = getAttrValue(a, sortKey);
      const valB = getAttrValue(b, sortKey);
      const strA = displayValue(valA);
      const strB = displayValue(valB);
      const cmp = strA.localeCompare(strB);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  if (isLoading) {
    return <div className="text-slate-500 py-8 text-center">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-sm text-slate-500">{sorted.length} records</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:text-slate-900 select-none"
                >
                  {col.label}
                  {sortKey === col.key && (sortDir === 'asc' ? ' ^' : ' v')}
                </th>
              ))}
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(record => (
              <tr key={record.Id} className="hover:bg-slate-50 transition-colors">
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-2.5 text-slate-700">
                    {formatCellValue(getAttrValue(record, col.key), col)}
                  </td>
                ))}
                <td className="px-4 py-2.5">
                  <Link
                    to={`/${entityName}/${record.Id}`}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-400">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
